(() => {
  if (window.AcCommerceRuntime) {
    window.AcCommerceRuntime.init();
    return;
  }

  const body = document.body;
  const lifecycle = new AbortController();
  const productGalleryControllers = new Map();
  const productYouTubeControllers = new Map();
  const recentControllers = new Map();
  const rootsWithin = (scope, selector) => [
    ...(scope?.matches?.(selector) ? [scope] : []),
    ...scope?.querySelectorAll?.(selector) || []
  ];
  const RECENT_KEY = 'argue-culture-recent-products-v2';
  const LEGACY_RECENT_KEY = 'argue-culture-recent-products-v1';
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => body.dataset.acMotion === 'reduced' || motionPreference.matches;

  const initProductGallery = (scope = document) => {
    const product = scope?.matches?.('[data-ac-ref-product]') ? scope : scope?.querySelector?.('[data-ac-ref-product]');
    const stage = product?.querySelector('[data-ac-ref-product-media-stage]');
    const gallery = product?.querySelector('[data-ac-ref-product-gallery]');
    const media = gallery ? [...gallery.querySelectorAll('[data-ac-ref-product-media]')] : [];
    const thumbnails = product ? [...product.querySelectorAll('[data-ac-ref-product-thumbnail]')] : [];
    const previous = product?.querySelector('[data-ac-ref-product-media-previous]');
    const next = product?.querySelector('[data-ac-ref-product-media-next]');
    const current = product?.querySelector('[data-ac-ref-product-pagination-current]');
    if (!product || !stage || !gallery || !media.length || !window.Swiper) return;
    const existing = productGalleryControllers.get(product);
    if (existing) {
      existing.refresh();
      return;
    }

    let destroyed = false;
    const pauseInactiveMedia = (index) => {
      if (destroyed) return;
      media.forEach((item, itemIndex) => item.querySelectorAll('video').forEach((video) => {
        if (itemIndex === index && !document.hidden && !reduced()) video.play().catch(() => {});
        else video.pause();
      }));
    };
    let activeIndex = 0;
    const controller = window.AcProductGallery.create({
      scope: product, stage, track: gallery, slides: media, thumbnails, previous, next, current,
      mediaId: (item) => item.dataset.acRefProductMediaId,
      initialMediaId: product.dataset.acActiveVariantMediaId,
      onChange: (index, { emit }) => {
        activeIndex = index;
        pauseInactiveMedia(index);
        if (emit) product.dispatchEvent(new CustomEvent('ac:media-change', { detail: { index, media: media[index] } }));
      },
    });
    const onVariantChange = (event) => controller.selectMedia(event.detail?.featuredMediaId);
    const onVisibility = () => pauseInactiveMedia(activeIndex);
    product.addEventListener('ac:variant-change', onVariantChange);
    document.addEventListener('visibilitychange', onVisibility);
    motionPreference.addEventListener('change', onVisibility);
    product.dataset.acCommerceGalleryBound = 'true';
    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      media.forEach((item) => item.querySelectorAll('video').forEach((video) => video.pause()));
      controller.destroy();
      product.removeEventListener('ac:variant-change', onVariantChange);
      document.removeEventListener('visibilitychange', onVisibility);
      motionPreference.removeEventListener('change', onVisibility);
      delete product.dataset.acCommerceGalleryBound;
      productGalleryControllers.delete(product);
    };
    productGalleryControllers.set(product, { refresh: controller.refresh, syncMotion: onVisibility, destroy });
  };

  const initProductYouTube = (scope = document) => {
    const product = scope?.matches?.('[data-ac-ref-product]') ? scope : scope?.querySelector?.('[data-ac-ref-product]');
    const roots = product ? [...product.querySelectorAll('[data-ac-youtube-player]')] : [];
    if (!roots.length) return;
    if (productYouTubeControllers.has(product)) {
      productYouTubeControllers.get(product).refresh();
      return;
    }
    const abort = new AbortController();
    const { signal } = abort;
    const floating = product.querySelector('[data-ac-youtube-float]');
    let destroyed = false;
    let suspended = false;
    let floatShown = false;
    let floatClosed = false;
    let floatTimer;
    const states = roots.map(root => ({ root, iframe: root.querySelector('[data-ac-youtube-iframe]'),
      role: root.dataset.acYoutubeRole, player: null, ready: false, bound: false, failed: false,
      autoplayBlocked: false, manual: false, apiFailed: false, ratio: 0 }));
    const main = () => states.find(state => state.role === 'main'
      && state.root.closest('[data-ac-ref-product-media]')?.getAttribute('aria-hidden') === 'false');
    const active = state => !destroyed && !suspended && !document.hidden && (state.role === 'main'
      ? main() === state : floatShown && !floatClosed && !main() && state.ratio >= 0.5);
    const canAutoplay = state => active(state) && !reduced() && state.ratio >= 0.5
      && !state.failed && !state.autoplayBlocked;
    const clearError = state => {
      state.failed = state.autoplayBlocked = false;
      delete state.root.dataset.acYoutubeError;
      delete state.root.dataset.acYoutubeErrorCode;
    };
    const diagnose = (state, reason, code = null, fatal = true) => {
      state.root.dataset.acYoutubeError = reason;
      if (code !== null) state.root.dataset.acYoutubeErrorCode = String(code);
      else delete state.root.dataset.acYoutubeErrorCode;
      state.root.dispatchEvent(new CustomEvent('ac:youtube-error', { bubbles: true,
        detail: { reason, code, fatal, videoId: state.root.dataset.acYoutubeId, role: state.role } }));
    };
    const pause = state => {
      state.manual = false;
      if (state.ready) state.player.pauseVideo();
      else if (state.apiFailed && state.iframe.hasAttribute('src')) {
        // A native iframe still works when the optional API is blocked; unload it to stop it.
        state.iframe.removeAttribute('src');
        state.root.classList.remove('is-loaded');
      }
    };
    const play = state => {
      if (!state.ready || !active(state) || (!state.manual && !canAutoplay(state))) return;
      states.filter(other => other !== state).forEach(pause);
      if (state.manual) state.player.unMute();
      else state.player.mute();
      state.manual = false;
      state.player.playVideo();
    };
    const load = state => {
      if (!state.iframe.hasAttribute('src')) {
        state.iframe.src = state.iframe.dataset.src;
        state.root.classList.add('is-loaded');
      }
      if (state.bound) { play(state); return; }
      state.bound = true;
      // Standard iframe is the player. The official API only adds playback coordination.
      window.AcYouTubeApi.load().then(YT => {
        if (destroyed) return;
        state.player = new YT.Player(state.iframe, { events: {
          onReady: () => {
            if (destroyed) return;
            state.ready = true;
            if (active(state)) play(state);
            else pause(state);
          },
          onStateChange: event => {
            if (destroyed || event.data !== YT.PlayerState.PLAYING) return;
            clearError(state);
            if (active(state)) states.filter(other => other !== state).forEach(pause);
            else pause(state);
          },
          onError: event => {
            if (destroyed) return;
            const code = Number(event.data);
            state.failed = true;
            // 150 is not enough to distinguish permission denial from a login/bot challenge.
            diagnose(state, code === 153 ? 'referrer' : 'playerError', Number.isFinite(code) ? code : null);
          },
          onAutoplayBlocked: () => {
            if (destroyed) return;
            state.autoplayBlocked = true;
            diagnose(state, 'autoplayBlocked', null, false);
          }
        } });
      }).catch(() => {
        if (destroyed) return;
        state.apiFailed = true;
        diagnose(state, 'apiLoad', null, false);
        if (!active(state)) pause(state);
      });
    };
    const sync = () => {
      if (destroyed) return;
      floating?.classList.toggle('is-main-active', Boolean(main()));
      for (const state of states) {
        if (active(state)) {
          load(state);
          if (reduced() || state.ratio < 0.5) pause(state);
        }
        else pause(state);
      }
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const state = states.find(item => item.root === entry.target);
        state.ratio = entry.intersectionRatio;
      }
      sync();
    }, { threshold: [0, 0.5, 1] });
    for (const state of states) {
      state.root.classList.add('is-enhanced');
      observer.observe(state.root);
      state.root.querySelector('[data-ac-youtube-poster-play]')?.addEventListener('click', () => {
        state.manual = true;
        clearError(state);
        load(state);
        state.iframe.focus({ preventScroll: true });
      }, { signal });
    }
    floating?.querySelector('[data-ac-youtube-float-close]')?.addEventListener('click', event => {
      floatClosed = true;
      floating.hidden = true;
      sync();
      if (event.detail === 0) product.querySelector('[data-ac-ref-product-media-stage]')?.focus({ preventScroll: true });
    }, { signal });
    const schedule = () => {
      const connection = navigator.connection;
      if (!floating || connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)) return;
      floatTimer = setTimeout(() => {
        floatShown = true;
        floating.hidden = false;
        sync();
      }, Math.max(0, Number(floating.dataset.acYoutubeDelay ?? 8) || 0) * 1000);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true, signal });
    product.addEventListener('ac:media-change', sync, { signal });
    document.addEventListener('visibilitychange', sync, { signal });
    motionPreference.addEventListener('change', sync, { signal });
    const refresh = () => { suspended = false; sync(); };
    const suspend = () => { suspended = true; states.forEach(pause); };
    const destroy = () => {
      destroyed = true;
      clearTimeout(floatTimer);
      abort.abort();
      observer.disconnect();
      for (const state of states) {
        const parent = state.iframe.parentElement;
        state.player?.destroy();
        state.iframe.removeAttribute('src');
        if (parent && !state.iframe.isConnected) parent.append(state.iframe);
        state.root.classList.remove('is-enhanced', 'is-loaded');
        clearError(state);
      }
      productYouTubeControllers.delete(product);
    };
    productYouTubeControllers.set(product, { refresh, syncMotion: sync, suspend, destroy });
    sync();
  };

  const readRecentProducts = () => {
    try {
      const stored = window.localStorage.getItem(RECENT_KEY) || window.localStorage.getItem(LEGACY_RECENT_KEY) || '[]';
      const saved = JSON.parse(stored);
      if (!Array.isArray(saved)) return [];
      return saved
        .map((item) => ({ id: String(item?.id || ''), url: String(item?.url || '') }))
        .filter((item) => item.id && item.url);
    } catch (error) {
      return [];
    }
  };

  const writeRecentProducts = (products) => {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(products));
      window.localStorage.removeItem(LEGACY_RECENT_KEY);
    } catch (error) {
      /* Storage is optional. */
    }
  };

  const wrapRecentCard = (card) => card ? `<li class="ac-product-list__item">${card}</li>` : '';

  const fetchRecentCard = async (item, signal) => {
    const url = new URL(item.url, window.location.origin);
    url.searchParams.set('section_id', 'ac-product-card-render');
    const response = await fetch(url, { headers: { Accept: 'text/html' }, signal });
    if (!response.ok) throw new Error(`Unable to render recent product ${item.id}`);
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    return wrapRecentCard(parsed.querySelector('[data-ac-recent-product-card]')?.innerHTML.trim() || '');
  };

  const initRecentProducts = (recentSection) => {
    const current = document.querySelector('[data-ac-commerce-current-product]');
    const recentRoot = recentSection?.querySelector('[data-ac-commerce-recent-list]');
    const list = recentRoot?.querySelector('.ac-product-list__items');
    if (!current || !recentSection || !recentRoot || !list || recentControllers.has(recentSection)) return;
    const item = {
      id: String(current.dataset.acCommerceProductId || ''),
      url: current.dataset.acCommerceProductUrl || '',
    };
    if (!item.id || !item.url) return;
    const request = new AbortController();
    let cancelNear;
    recentControllers.set(recentSection, {
      destroy: () => { cancelNear?.(); request.abort(); recentControllers.delete(recentSection); }
    });
    const history = readRecentProducts().filter((entry) => String(entry.id) !== String(item.id));
    writeRecentProducts([item, ...history].slice(0, 12));
    const products = history.slice(0, 8);
    if (!products.length) return;
    const load = async () => {
      const cards = new Array(products.length);
      let next = 0;
      const worker = async () => {
        while (next < products.length && !request.signal.aborted) {
          if (!recentSection.isConnected) {
            recentControllers.get(recentSection)?.destroy();
            return;
          }
          const index = next++;
          try { cards[index] = await fetchRecentCard(products[index], request.signal); }
          catch { cards[index] = ''; }
        }
      };
      await Promise.all(Array.from({ length: Math.min(2, products.length) }, worker));
      if (request.signal.aborted || !cards.some(Boolean) || !list.isConnected) return;
      list.innerHTML = cards.filter(Boolean).join('');
      recentSection.hidden = false;
      recentRoot.querySelector('ac-product-rail')?.refresh();
    };
    if (window.AcProductController?.whenNearProductEnd) {
      cancelNear = window.AcProductController.whenNearProductEnd(recentSection, load);
    } else load();
  };

  const init = (scope = document) => {
    rootsWithin(scope, '[data-ac-ref-product]').forEach((product) => {
      initProductGallery(product);
      initProductYouTube(product);
    });
    rootsWithin(scope, '[data-ac-commerce-recent-section]').forEach((section) => initRecentProducts(section));
  };
  const controllerMaps = [productGalleryControllers, productYouTubeControllers, recentControllers];
  const destroyWithin = (scope) => {
    for (const controllers of controllerMaps) {
      for (const [root, controller] of controllers) {
        if (root === scope || scope.contains(root)) controller.destroy();
      }
    }
  };
  const syncMotion = () => {
    productGalleryControllers.forEach((controller) => controller.syncMotion());
    productYouTubeControllers.forEach((controller) => controller.syncMotion());
  };
  const motionObserver = new MutationObserver(syncMotion);
  motionObserver.observe(body, { attributes: true, attributeFilter: ['data-ac-motion'] });
  const destroy = () => {
    lifecycle.abort();
    motionObserver.disconnect();
    controllerMaps.forEach((controllers) => controllers.forEach((controller) => controller.destroy()));
    delete window.AcCommerceRuntime;
  };
  window.AcCommerceRuntime = { init, destroyWithin, destroy };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target), { signal: lifecycle.signal });
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target), { signal: lifecycle.signal });
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted) {
      destroy();
      return;
    }
    productYouTubeControllers.forEach((controller) => controller.suspend());
  }, { signal: lifecycle.signal });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    productGalleryControllers.forEach((controller) => controller.refresh());
    productYouTubeControllers.forEach((controller) => controller.refresh());
  }, { signal: lifecycle.signal });
})();
